import {
  type ComponentClass,
  type FunctionComponent,
  type ReactElement,
} from 'react';
import {
  type GDSInputStatus,
  type GDSStyleObject,
} from '@leagueplatform/genesis-core';
import { type NarrowByType } from './util.types';
import {
  type MasonryEngineNodeBackendStates,
  type MasonryEngineNodeStates,
} from './masonry-engine-state.types';

type UnknownValueMap = Record<string, unknown>;
type BaseAnalyticsObject = {
  event_name: string;
  payload: Record<string, unknown>;
};

declare const ResultDataBrand: unique symbol;

export type MasonryEngineNodeAction<
  Type extends string = string,
  Payload extends UnknownValueMap = UnknownValueMap,
  Analytics extends BaseAnalyticsObject = BaseAnalyticsObject,
  ResultData extends unknown = unknown,
> = {
  type: Type;
  namespace: string;
  payload: Payload;
  analytics?: Analytics;
  readonly [ResultDataBrand]?: [ResultData];
  onSuccess?: MasonryEngineNodeAction;
  onFailure?: MasonryEngineNodeAction;
  onException?: MasonryEngineNodeAction;
};

export type ActionResultData<Action extends MasonryEngineNodeAction> =
  Action extends {
    readonly [ResultDataBrand]?: [infer R];
  }
    ? R
    : never;

export type AnyMasonryEngineNodeAction<Type extends string = string> =
  MasonryEngineNodeAction<Type, any, any, any>;

export type MasonryNodeThemeConfiguration = Record<string, unknown>;

/**
 * A single MasonryEngine node, with a getter for the node's data (type and attributes).
 * @typedef {object} MasonryEngineNode - A single MasonryEngine node, with a getter for the node's data (type and attributes).
 * @property {string} id - id of MasonryEngine
 * @property {Type} type - type of MasonryEngine.
 * @property {object} properties - an object for content of MasonryEngine
 * @property {Record<string, Array<MasonryEngine<NodeType>>>} sections - a map for Children of MasonryEngineNode type
 * @property {object asyncConfig=} asyncConfig - optional async configuration for the MasonryEngineNode
 */

export const HAS_BEEN_TRANSFORMED = Symbol('isPostTransform');

export type MasonryEngineNodeAsyncConfig<
  GetterParams extends unknown = unknown,
> = {
  getterParams: GetterParams;
  initialQueryParams?: MasonryEngineNodeQueryParams;
};

export type MasonryEngineNode<
  Type extends string = string,
  Properties extends {} = {},
  Actions extends Record<string, MasonryEngineNodeAction> = {},
  Sections extends Record<string, Array<MasonryEngineNode>> = {},
  GetterParams extends unknown = unknown,
> = {
  id: string;
  locatorId?: string;
  type: Type;
  properties: Properties;
  states?: MasonryEngineNodeBackendStates;
  actions?: Actions;
  namespace?: string;
  sections: Sections;
  themeConfiguration?: MasonryNodeThemeConfiguration;
  asyncConfig?: MasonryEngineNodeAsyncConfig<GetterParams>;
  [HAS_BEEN_TRANSFORMED]?: boolean;
  prefetchNodes?: MasonryEngineNode[];
};

export type NodeQueryMeta = {
  ancestry: MasonryEngineNodeAncestry;
};

export type AnyMasonryEngineNode = MasonryEngineNode<
  string,
  Record<string, unknown>,
  Record<string, AnyMasonryEngineNodeAction>,
  Record<string, Array<AnyMasonryEngineNode>>,
  unknown
>;

type ReactComponent<Props extends {} = {}> =
  | FunctionComponent<Props>
  | ComponentClass<Props>;

/**
 * A generic structure for props of each renderer for MasonryEngine which will
 * have all properties from {@link MasonryEngineNode `MasonryEngineNode`} and sections as React Children for some
 * container nodes like Layout or Carousel.
 */
export type MasonryEngineNodeRendererProps<Node extends MasonryEngineNode> =
  Node['properties'] &
    MasonryEngineNodeStates &
    Node['actions'] &
    Record<keyof Node['sections'], ReactElement[]> & {
      /**
       * The ID of the node.
       */
      nodeId: Node['id'];
      /**
       * A ref to be passed to the root DOM element rendered by this renderer.
       */
      rootElementRef: React.RefCallback<HTMLElement>;
      /**
       * The original node data object.
       */
      nodeData: Node;
      /**
       * Whether this node is currently being reloaded as a result of a `reload` action.
       */
      isReloading?: boolean;
      /**
       * An id used to locate this node in the DOM, which can be used for testing purposes. Will default to `{nodeType}${nodeId}`
       */
      locatorId?: Node['locatorId'];
      /**
       * Optional class name to apply to the root element of the node.
       */
      className?: string;
      /**
       * A class name for engine-level styling that may or may not be present.
       */
      engineClassName?: string;
      /**
       * Optional CSS styles to apply to the root element of the node.
       */
      css?: GDSStyleObject;
    };

/**
 * A React component rendering a resolved {@link MasonryEngineNode `MasonryEngineNode`}.
 * React component [MasonryEngineNodeRenderer] accepts all properties and sections as a map of children.
 */
export type MasonryEngineNodeRenderer<Node extends MasonryEngineNode> =
  ReactComponent<MasonryEngineNodeRendererProps<Node>>;

export type MasonryEngineNodeRendererMap<
  /**
   * A union of `MasonryEngineNode`s. Ensures that all of them are represented
   * in the map, and that for each one, an appropriate renderer is provided (e.g.
   * expecting the same props).
   */
  Node extends MasonryEngineNode,
> = {
  [type in Node['type']]: MasonryEngineNodeRenderer<NarrowByType<Node, type>>;
};

/**
 * A map of all sdk namespace and their renderers maps. This renderer map is always expected to have
 * a namespace called `default` which will define all default renderer for that MasonryEngine instance and
 * other maps for capabilities modules.
 */
export type MasonryEngineNamespacedNodeRendererMap<
  /**
   * A union of `MasonryEngineNode`s. Ensures that all of them are represented
   * in the map, and that for each one, an appropriate renderer is provided (e.g.
   * expecting the same props).
   */
  Node extends MasonryEngineNode,
> = {
  default: MasonryEngineNodeRendererMap<Node>;
} & Partial<Record<string, Partial<MasonryEngineNodeRendererMap<Node>>>>;

/**
 * An array that describes the ancestry {@link MasonryEngineNode `MasonryEngineNode`},
 * ending with its own ID.
 */
export type MasonryEngineNodeAncestry = MasonryEngineNode['id'][];

export type ValidationStatusType = 'error' | 'warning' | 'success';

export type Validation<
  Name extends string = string,
  Value extends unknown = unknown,
> = {
  statusMessage?: string;
  statusType: ValidationStatusType;
  validationType: Name;
  payload: {
    [key in Name]: Value;
  };
};

/**
 * Properties shared by all Masonry Form Fields.
 *
 */

export type BaseFormFieldNodeProperties<TValue extends unknown = string> = {
  formFieldProperties: {
    formFieldId: string;
    formId: string;
    label: string;
    name: string;
    formFieldType?: string;
    hideLabel?: boolean;
    hint?: string;
    inputStatus?: GDSInputStatus;
    showOptionalFlag?: boolean;
    statusMessage?: string;
    tooltipLabel?: string;
    tooltipContent?: string;
    tooltipIcon?: string;
    validationRules?: Validation[];
  };
  value?: TValue;
};

/**
 * A single MasonryEngine form field node.
 *
 */
export type MasonryEngineFormFieldNode<
  Type extends string = string,
  Actions extends {} = {},
> = MasonryEngineNode<Type, BaseFormFieldNodeProperties, Actions>;

export type MasonryEngineNodeQueryParams = Record<string, string>;
